import React, { FC, useEffect, useState } from 'react';
import styled, { keyframes } from 'styled-components';

import { Icon, formatNumber } from '@column/column-ui-kit';

import { CopyRow } from '~/components/CopyRow';
import { Loan } from '~/repositories';
import { Card, Flex, Inner, Text } from '~/styles';

interface LoanBalancesBreakdownProps {
  balances?: Loan['balances'];
  currencyCode: string;
}

const CopyRowList = styled.ul`
  display: flex;
  flex-direction: column;
  padding: 0 1px;

  li {
    border-top: 1px solid ${({ theme }) => theme.secondary.blendToBackground(150)};

    &:first-child {
      border-top: none;
    }
  }
`;

const parseBalance = (value: string | undefined): number => {
  if (!value) return 0;
  const parsed = parseFloat(value);
  return isNaN(parsed) ? 0 : parsed;
};

interface FieldProps {
  value: string | React.ReactNode;
  variant?: 'secondary' | 'default';
  isHidden?: boolean;
}

const FieldVisibilityButton = styled.button`
  display: flex;
  align-items: center;
  white-space: nowrap;
  gap: 4px;
  background: none;
  border: none;
  color: ${({ theme }) => theme.secondary.blendToBackground(500)};
  cursor: pointer;
  font-size: 12px;

  &:hover {
    color: ${({ theme }) => theme.secondary.blendToBackground(700)};
  }
`;

const fadeIn = keyframes`
  from {
    opacity: 0;
    max-height: 0;
  }
  to {
    opacity: 1;
    max-height: 1000px;
  }
`;

const fadeOut = keyframes`
  from {
    opacity: 1;
    max-height: 1000px;
  }
  to {
    opacity: 0;
    max-height: 0;
  }
`;

const AnimatedListItem = styled.li<{ isVisible: boolean }>`
  animation: ${({ isVisible }) => (isVisible ? fadeIn : fadeOut)} 0.3s ease-in-out forwards;
  overflow: hidden;
`;

const AnimatedField: FC<{ field: FieldProps; label: string; isVisible: boolean }> = ({ field, label, isVisible }) => {
  const [shouldRender, setShouldRender] = useState(isVisible);

  useEffect(() => {
    if (isVisible) {
      setShouldRender(true);
    } else {
      const timer = setTimeout(() => setShouldRender(false), 300);
      return () => clearTimeout(timer);
    }
  }, [isVisible]);

  if (!shouldRender) return null;

  return (
    <AnimatedListItem isVisible={isVisible}>
      <CopyRow label={label} value={field.value} variant={field.variant} />
    </AnimatedListItem>
  );
};

export const LoanBalancesBreakdown: React.FC<LoanBalancesBreakdownProps> = ({ balances, currencyCode }) => {
  if (!balances) {
    return (
      <Card>
        <CopyRowList>
          <li>
            <CopyRow label="Balances" value="Balance information not available" />
          </li>
        </CopyRowList>
      </Card>
    );
  }

  const principalOutstanding = parseBalance(balances.principalOutstanding);
  const principalPaid = parseBalance(balances.principalPaid);
  const principalChargedOff = parseBalance(balances.principalChargedOff);
  const principalHolding = parseBalance(balances.principalHolding);
  const retainedPrincipalOutstanding = parseBalance((balances as any).retainedPrincipalOutstanding);
  const retainedPrincipalPaid = parseBalance((balances as any).retainedPrincipalPaid);
  const retainedPrincipalChargedOff = parseBalance((balances as any).retainedPrincipalChargedOff);
  const interestReceivable = parseBalance((balances as any).interestReceivable);
  const retainedInterestReceivable = parseBalance((balances as any).retainedInterestReceivable);
  const interestPaid = parseBalance((balances as any).interestPaid);
  const retainedInterestPaid = parseBalance((balances as any).retainedInterestPaid);
  const servicingFeePayable = parseBalance((balances as any).servicingFeePayable);

  const totalPrincipal = principalOutstanding + principalPaid + principalChargedOff + principalHolding;

  const getFieldProps = (): Record<string, FieldProps> => {
    const fields: Record<string, FieldProps> = {
      principalOutstanding: {
        value: formatNumber(principalOutstanding, currencyCode),
      },
      retainedPrincipalOutstanding: {
        value: formatNumber(retainedPrincipalOutstanding, currencyCode),
        variant: 'secondary',
        isHidden: retainedPrincipalOutstanding === 0,
      },
      principalHolding: {
        value: formatNumber(principalHolding, currencyCode),
        variant: 'secondary',
        isHidden: principalHolding === 0,
      },
      principalPaid: {
        value: formatNumber(principalPaid, currencyCode),
        variant: 'secondary',
        isHidden: principalPaid === 0,
      },
      retainedPrincipalPaid: {
        value: formatNumber(retainedPrincipalPaid, currencyCode),
        variant: 'secondary',
        isHidden: retainedPrincipalPaid === 0,
      },
      principalChargedOff: {
        value: formatNumber(principalChargedOff, currencyCode),
        variant: 'secondary',
        isHidden: principalChargedOff === 0,
      },
      retainedPrincipalChargedOff: {
        value: formatNumber(retainedPrincipalChargedOff, currencyCode),
        variant: 'secondary',
        isHidden: retainedPrincipalChargedOff === 0,
      },
      interestReceivable: {
        value: formatNumber(interestReceivable, currencyCode),
        isHidden: interestReceivable === 0,
      },
      retainedInterestReceivable: {
        value: formatNumber(retainedInterestReceivable, currencyCode),
        variant: 'secondary',
        isHidden: retainedInterestReceivable === 0,
      },
      interestPaid: {
        value: formatNumber(interestPaid, currencyCode),
        variant: 'secondary',
        isHidden: interestPaid === 0,
      },
      retainedInterestPaid: {
        value: formatNumber(retainedInterestPaid, currencyCode),
        variant: 'secondary',
        isHidden: retainedInterestPaid === 0,
      },
      servicingFeePayable: {
        value: formatNumber(servicingFeePayable, currencyCode),
        variant: 'secondary',
        isHidden: servicingFeePayable === 0,
      },
    };

    return fields;
  };

  const [showHiddenFields, setShowHiddenFields] = useState(false);
  const fieldProps = getFieldProps();
  const hasHiddenFields = Object.values(fieldProps).some((field) => field.isHidden);

  const toggleFieldVisibility = (e: React.MouseEvent) => {
    e.preventDefault();
    setShowHiddenFields(!showHiddenFields);
  };

  const fieldLabels: Record<string, string> = {
    principalOutstanding: 'Principal Outstanding',
    retainedPrincipalOutstanding: 'Retained Principal Outstanding',
    principalHolding: 'Principal Holding',
    principalPaid: 'Principal Paid',
    retainedPrincipalPaid: 'Retained Principal Paid',
    principalChargedOff: 'Principal Charged Off',
    retainedPrincipalChargedOff: 'Retained Principal Charged Off',
    interestReceivable: 'Interest Receivable',
    retainedInterestReceivable: 'Retained Interest Receivable',
    interestPaid: 'Interest Paid',
    retainedInterestPaid: 'Retained Interest Paid',
    servicingFeePayable: 'Servicing Fee Payable',
  };

  return (
    <Card>
      <CopyRowList>
        {Object.entries(fieldProps).map(([key, field]) => (
          <AnimatedField
            key={key}
            field={field}
            label={fieldLabels[key]}
            isVisible={!field.isHidden || showHiddenFields}
          />
        ))}
        <li>
          <Inner py={12} px={16}>
            <Flex alignItems="center">
              <Flex auto>
                <Text as="p" size={12} color="lighter">
                  {totalPrincipal > 0 && principalOutstanding > 0 && principalPaid > 0
                    ? `Principal Outstanding (${formatNumber(principalOutstanding, currencyCode)}) + Principal Paid (${formatNumber(principalPaid, currencyCode)})${principalChargedOff > 0 ? ` + Principal Charged Off (${formatNumber(principalChargedOff, currencyCode)})` : ''}${principalHolding > 0 ? ` + Principal Holding (${formatNumber(principalHolding, currencyCode)})` : ''} = Total Principal Activity`
                    : interestReceivable > 0 && interestPaid > 0
                      ? `Interest Receivable (${formatNumber(interestReceivable, currencyCode)}) - Interest Paid (${formatNumber(interestPaid, currencyCode)}) = Net Interest`
                      : 'Represents the current state of principal and interest amounts'}
                </Text>
              </Flex>

              {hasHiddenFields && (
                <FieldVisibilityButton onClick={toggleFieldVisibility}>
                  {showHiddenFields ? 'Less details' : 'More details'}
                  {showHiddenFields ? <Icon.EyeClosed /> : <Icon.Eye />}
                </FieldVisibilityButton>
              )}
            </Flex>
          </Inner>
        </li>
      </CopyRowList>
    </Card>
  );
};
